import { CardMedia, createStyles, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { ReactComponent as ArrowTopRightSvg } from '../../../assets/arrow_top_right.svg';
import { LinkPreviewMetadataFragment } from '../../../graphql/gen/types';
import { spacings, TextSizesEnum } from '../../../theme';
import { getLinkData } from '../../../utils/links';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { usePlexRoute } from '../details/usePlexRoute';
import { ExternalLinkWrapper } from './ExternalLinkWrapper';

type Props = {
  // TODO: disabled (not clickable / linked) state
  // TODO: loading state
  openPlexLinksInNewTab?: boolean;
  linkMetadata: LinkPreviewMetadataFragment;
};

export const LinkPreviewBox = ({ openPlexLinksInNewTab, linkMetadata }: Props): React.ReactElement => {
  const classes = useStyles();
  const [, updatePlexRoute] = usePlexRoute();

  switch (linkMetadata.details.__typename) {
    case 'ImageLinkMetadata': {
      const { url } = linkMetadata.details;
      return (
        <ExternalLinkWrapper href={url} className={classes.media}>
          <CardMedia className={classes.mediaPicture} image={url} />
        </ExternalLinkWrapper>
      );
    }
    case 'PlexFactLinkMetadata':
    case 'PlexPostLinkMetadata':
    case 'AttachmentLinkMetadata':
    case 'GoogleDocLinkMetadata':
    case 'GoogleDriveFileLinkMetadata':
    case 'GoogleSheetsLinkMetadata':
    case 'GoogleSlidesLinkMetadata':
    case 'GenericLink':
    case 'InvalidLink': {
      const { title, subtitle, plexContentLink, LinkIcon } = getLinkData(linkMetadata.details);
      let mediaView: React.ReactElement = (
        <PlexButton
          className={classNames(classes.media, classes.mediaLink)}
          label={
            <div className={classes.mediaContent}>
              <div className={classes.mediaContentTop}>
                <LinkIcon />
                <ArrowTopRightSvg />
              </div>
              <div className={classes.mediaContentBottom}>
                <PlexTypography className={classes.mediaTitle} size={TextSizesEnum.Small} text={title} maxLine={2} />
                <PlexTypography
                  className={classes.mediaSubTitle}
                  size={TextSizesEnum.XXSmall}
                  text={subtitle}
                  maxLine={2}
                />
              </div>
            </div>
          }
          color={ButtonColor.TransparentGray}
          onClick={(): void => {
            if (plexContentLink != null && !openPlexLinksInNewTab) {
              updatePlexRoute(plexContentLink);
            }
          }}
        />
      );
      if (openPlexLinksInNewTab || plexContentLink == null) {
        mediaView = <ExternalLinkWrapper href={linkMetadata.id}>{mediaView}</ExternalLinkWrapper>;
      }

      return mediaView;
    }
  }
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-between',
    },
    content: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      margin: 0,
      padding: '16px 24px',
    },
    contentText: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    media: {
      flexShrink: 0,
      width: 136,
      height: 106,
      marginTop: spacings.xlarge,
      marginLeft: spacings.xlarge,
      marginRight: spacings.xlarge,
      padding: 0,
      justifyContent: 'flex-start',
      borderRadius: spacings.small,
    },
    mediaPicture: {
      width: '100%',
      height: '100%',
      borderRadius: spacings.small,
    },
    mediaLink: {
      border: '2px solid #EAEAEA',
    },
    mediaContent: {
      display: 'flex',
      height: '100%',
      width: '100%',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: spacings.small,
      overflow: 'hidden',
    },
    mediaContentTop: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    mediaContentBottom: {
      display: 'flex',
      flexDirection: 'column',
    },
    mediaTitle: {
      color: '#1E1F20',
    },
    mediaSubTitle: {
      color: '#7F85A2',
    },
  })
);
